"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export const DUBSADO_SCHEDULER_URL =
  "https://hello.dubsado.com:443/public/appointment-scheduler/69f12d298660164e1d557461/schedule";
export const ALT_DUBSADO_SCHEDULER_URL =
  "https://hello.dubsado.com:443/public/appointment-scheduler/6aa897587fc4536f4e19d14d/schedule?isIframe=true";
export const BOOK_PAGE_PATH = "/book";

interface BookingContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  openBooking: () => void;
  schedulerUrl: string;
  setSchedulerUrl: (url: string) => void;
  headingOverride: string | null;
  setHeadingOverride: (heading: string | null) => void;
}

const BookingContext = React.createContext<BookingContextValue | null>(null);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [schedulerUrl, setSchedulerUrl] = React.useState(DUBSADO_SCHEDULER_URL);
  const [headingOverride, setHeadingOverride] = React.useState<string | null>(null);
  const value = React.useMemo(
    () => ({ open, setOpen, openBooking: () => setOpen(true), schedulerUrl, setSchedulerUrl, headingOverride, setHeadingOverride }),
    [open, schedulerUrl, headingOverride],
  );

  return (
    <BookingContext.Provider value={value}>
      {children}
      <BookingModal open={open} onOpenChange={setOpen} heading={headingOverride ?? undefined} />
    </BookingContext.Provider>
  );
}

/**
 * Mount on a page to swap the booking modal's heading while that page is
 * active. Restores the default heading on unmount.
 */
export function UseBookingHeading({ heading }: { heading: string }) {
  const { setHeadingOverride } = useBooking();
  React.useEffect(() => {
    setHeadingOverride(heading);
    return () => setHeadingOverride(null);
  }, [heading, setHeadingOverride]);
  return null;
}

/**
 * Mount on a page to swap the booking modal's scheduler URL while that page
 * is active. Restores the default scheduler on unmount.
 */
export function UseSchedulerUrl({ url }: { url: string }) {
  const { setSchedulerUrl } = useBooking();
  React.useEffect(() => {
    setSchedulerUrl(url);
    return () => setSchedulerUrl(DUBSADO_SCHEDULER_URL);
  }, [url, setSchedulerUrl]);
  return null;
}

export function useBooking() {
  const ctx = React.useContext(BookingContext);
  if (!ctx) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return ctx;
}

export function BookingInterceptor() {
  const { openBooking } = useBooking();

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.composedPath ? (e.composedPath()[0] as HTMLElement) : (e.target as HTMLElement);
      const link = target?.closest?.(`a[href^="${BOOK_PAGE_PATH}"]`);
      if (!link) return;

      // Let users open the page in a new tab or with modifiers.
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (link.hasAttribute("target") || link.hasAttribute("download")) return;

      const href = link.getAttribute("href") || BOOK_PAGE_PATH;
      const url = new URL(href, window.location.href);
      if (url.pathname !== BOOK_PAGE_PATH) return;

      e.preventDefault();
      openBooking();
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [openBooking]);

  return null;
}

export function BookingModal({
  open,
  onOpenChange,
  heading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heading?: string;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col overflow-hidden bg-card text-foreground shadow-2xl outline-none",
            "inset-0 md:inset-6 lg:inset-10 md:rounded-3xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Book a free call with Three Day Launch
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Choose a time for your free website strategy call with Three Day Launch.
          </DialogPrimitive.Description>

          <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3 md:px-6 md:py-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary md:text-xs">
                Three Day Launch
              </p>
              <h2 className="text-base font-semibold leading-tight md:text-lg">
                {heading ?? "Book your free call to claim your $99 special."}
              </h2>
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Close booking calendar"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/80"
              >
                <X className="h-5 w-5" />
              </button>
            </DialogPrimitive.Close>
          </div>

          <div className="relative min-h-0 flex-1">
            <BookingEmbed />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function BookingEmbed({ url }: { url?: string }) {
  const ctx = React.useContext(BookingContext);
  const schedulerUrl = url ?? ctx?.schedulerUrl ?? DUBSADO_SCHEDULER_URL;
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (!loaded) setFailed(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, [loaded]);

  return (
    <>
      {!loaded && !failed && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading calendar...</p>
        </div>
      )}

      {failed && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
          <p className="font-semibold text-foreground">Click below to open the calendar:</p>
          <a
            href={schedulerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-full bg-primary-gradient px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow transition-opacity hover:opacity-90"
          >
            Open Calendar
          </a>
        </div>
      )}

      <iframe
        key={schedulerUrl}
        src={schedulerUrl}
        title="Book a free call with Three Day Launch"
        className={cn("h-full w-full", !loaded && "opacity-0")}
        scrolling="yes"
        onLoad={() => {
          setLoaded(true);
          setFailed(false);
        }}
        allow="fullscreen"
      />
    </>
  );
}

export function BookCallLink({
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { openBooking } = useBooking();

  return (
    <a
      href={BOOK_PAGE_PATH}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        openBooking();
      }}
      className={className}
      {...props}
    >
      {children}
    </a>
  );
}
